import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw, LogOut, Menu, X } from 'lucide-react';
import './TopBar.css';

const API_BASE = '/api';

function TopBar({ month, setMonth, onSyncComplete, sidebarCollapsed, onToggleSidebar }) {
  const { user, token, logout, isSuperadmin } = useAuth();
  const location = useLocation();
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const isWfReport = location.pathname === '/wf-report';

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path === '/analytics') return 'Sales Overview';
    if (path === '/sales/team') return 'Sales Team';
    if (path.startsWith('/salesperson/')) return 'Salesperson Detail';
    if (path.startsWith('/sales-breakdown/')) return 'Sales Breakdown';
    if (path === '/jq-bookings') return 'JQ Bookings';
    if (path === '/zen-bookings') return 'Zen Bookings';
    if (path === '/commissions') return 'Commission Summary';
    if (path === '/commission-control') return 'Commission Admin';
    if (path === '/wf-report') return 'W Factory Report';
    if (path.startsWith('/pnl')) return 'Profit & Loss';
    if (path === '/marketing') return 'Lead Report';
    if (path === '/marketing/spend-report') return 'Spend Report';
    if (path === '/marketing/leads') return 'Key in Lead';
    if (path === '/marketing/spend') return 'Key in Spend';
    if (path === '/marketing/kpi') return 'Marketing Targets';
    if (path === '/settings/daily-forecast') return 'Forecast Settings';
    if (path === '/settings/month-closing') return 'Month Closing';
    if (path === '/overview/checks') return 'Data Checks';
    if (path === '/overview/customers') return 'Customer Balances';
    if (path === '/overview/suppliers') return 'Supplier Balances';
    if (path === '/settings/actuals') return 'Actuals Input';
    if (path === '/settings/bank-accounts') return 'Bank Accounts';
    if (path === '/forecast') return 'Target vs Actual';
    if (path === '/reports') return 'Monthly Reports';
    if (path === '/sync') return 'Sync Status';
    if (path === '/settings/users') return 'Users & Roles';
    if (path === '/settings/api') return 'API Connections';
    if (path.startsWith('/commission-guide/')) return 'Commission Guide';
    if (path === '/jq-commission') return 'JQ Commission';
    if (path === '/zen-commission') return 'Zen Commission';
    if (path === '/bonus') return 'Bonus Gate';
    if (path === '/change-password') return 'Change Password';
    if (path === '/meetings') return 'Sales Meetings';
    if (path.startsWith('/meetings/')) return 'Sales Meeting';
    return 'Dashboard';
  };

  const getSubtitle = () => {
    const path = location.pathname;
    if (path === '/') return "Welcome back! Here's your business overview.";
    if (path === '/analytics') return 'Team performance analytics and trends.';
    return '';
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      await axios.post(`${API_BASE}/sync/bukku`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.post(`${API_BASE}/sync/calculate`, { month }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncMsg('Full sync complete');
      if (onSyncComplete) onSyncComplete();
      setTimeout(() => setSyncMsg(null), 4000);
    } catch (err) {
      setSyncMsg('Sync failed. Try again or check Sync Status.');
    } finally {
      setSyncing(false);
    }
  };

  // Segmented month · year picker.
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [pYearStr, pMonthStr] = (month || '2026-05').split('-');
  const pYear = parseInt(pYearStr, 10) || 2026;
  const pMonth = parseInt(pMonthStr, 10) || 5; // 1-12
  const isSales = user && user.role === 'salesperson';
  const YEARS = [];
  for (let y = 2024; y <= new Date().getFullYear() + 1; y++) YEARS.push(y);
  const yearOptions = isSales ? YEARS.filter(y => y >= 2026) : YEARS;
  const segSelect = { appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', border: 'none', fontWeight: 700, fontSize: 15, padding: '8px 16px', cursor: 'pointer', outline: 'none' };

  return (
    <div className={`topbar ${location.pathname === '/' ? 'overview-toolbar' : ''}`}>
      <div className="topbar-left">
        <button className="mobile-menu-button" aria-label={sidebarCollapsed ? 'Open navigation' : 'Close navigation'} aria-expanded={!sidebarCollapsed} aria-controls="primary-sidebar" onClick={onToggleSidebar}>
          {sidebarCollapsed ? <Menu size={22} /> : <X size={22} />}
        </button>
        <div>
          <h1>{getPageTitle()}</h1>
          {getSubtitle() && <div className="topbar-subtitle">{getSubtitle()}</div>}
        </div>
      </div>
      <div className="topbar-right">
        {!isWfReport && <div className="topbar-monthyear" style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid var(--rmc-line)', borderRadius: 999, overflow: 'hidden', background: 'var(--rmc-white)' }}>
          <select
            aria-label="Month"
            value={pMonth - 1}
            onChange={(e) => { const idx = parseInt(e.target.value, 10); setMonth(`${pYear}-${String(idx + 1).padStart(2, '0')}`); }}
            style={{ ...segSelect, background: 'rgba(59, 91, 219,0.18)', color: 'var(--rmc-ink)' }}
          >
            {MONTHS_SHORT.map((m, idx) => (
              <option key={m} value={idx} disabled={isSales && pYear === 2026 && idx + 1 < 5}>{m}</option>
            ))}
          </select>
          <select
            aria-label="Year"
            value={pYear}
            onChange={(e) => { const yr = parseInt(e.target.value, 10); setMonth(`${yr}-${String(pMonth).padStart(2, '0')}`); }}
            style={{ ...segSelect, background: 'transparent', borderLeft: '1px solid var(--rmc-line)', color: 'var(--rmc-ink)' }}
          >
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>}

        {/* Full finance sync is a superadmin-only action. */}
        {isSuperadmin && !isWfReport && (
          <>
            {syncMsg && (
              <span role="status" className={`topbar-sync-msg ${syncMsg.includes('fail') ? 'error' : 'success'}`}>
                {syncMsg}
              </span>
            )}

            <button
              className="topbar-sync-btn"
              onClick={handleSync}
              disabled={syncing}
              title="Refresh Bukku sales and outstanding balances, then recalculate commissions"
            >
              <RefreshCw size={14} className={syncing ? 'spin' : ''} />
              {syncing ? 'Full sync running...' : 'Full sync & recalculate commissions'}
            </button>
          </>
        )}

        <div className="topbar-user-pill">
          <span className="topbar-user-name">{user?.name}</span>
          <span className="topbar-user-role">{user?.role}</span>
        </div>

        <button className="topbar-logout" onClick={logout} title="Logout" aria-label="Log out">
          <LogOut size={16} />
        </button>
      </div>
    </div>
  );
}

export default TopBar;
